import { useState } from "react";
import { useNotify } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  addProspectToList,
  createList,
  DEFAULT_LISTS,
  ensureProspect,
  getSessionAndSale,
  runPool,
  type DealList,
} from "./dealsCrmActions";
import type { DealLead } from "./types";

interface BulkListBarProps {
  items: DealLead[]; // resolved selected leads (visible ones)
  lists: DealList[];
  onListsChanged: () => void;
  onListedIdsChanged: () => void;
  onClear: () => void;
  prompt: (title: string) => Promise<string | null>;
}

/**
 * Appears when rows are selected: add-to-list only (quick lists
 * find-or-create + "New list…"). Concurrency mirrors map.html (4 inserts).
 */
export const BulkListBar = ({
  items,
  lists,
  onListsChanged,
  onListedIdsChanged,
  onClear,
  prompt,
}: BulkListBarProps) => {
  const notify = useNotify();
  const [progress, setProgress] = useState<string | null>(null);
  const busy = progress != null;

  const resolveListChoice = async (value: string): Promise<DealList | null> => {
    if (value === "__new") {
      const name = await prompt("New list");
      if (!name) return null;
      const created = await createList(name);
      if (created) onListsChanged();
      return created;
    }
    if (value.startsWith("__default:")) {
      const created = await createList(value.slice("__default:".length));
      if (created) onListsChanged();
      return created;
    }
    return lists.find((l) => String(l.id) === value) ?? null;
  };

  const bulkAddToList = async (value: string) => {
    const choice = await resolveListChoice(value);
    if (!choice) return;
    const ctx = await getSessionAndSale();
    if (!ctx) {
      notify("Sign in first", { type: "warning" });
      return;
    }
    let ok = 0;
    let fail = 0;
    let done = 0;
    await runPool(items, 4, async (item) => {
      try {
        const pid = await ensureProspect(ctx, item);
        if (pid && (await addProspectToList(ctx, pid, choice.id))) ok++;
        else fail++;
      } catch (e) {
        fail++;
        console.error(e);
      }
      setProgress(`Processing ${++done}/${items.length}…`);
    });
    setProgress(null);
    notify(`Added ${ok} to ${choice.name}${fail ? ` · ${fail} failed` : ""}`, {
      type: fail ? "warning" : "info",
    });
    onListedIdsChanged();
    onClear();
  };

  const custom = lists.filter((l) => !DEFAULT_LISTS.includes(l.name));

  return (
    <div className="flex flex-wrap items-center gap-2 border-t bg-secondary px-3 py-2">
      <span className="text-sm font-semibold">
        {progress ?? `${items.length} selected`}
      </span>
      <Select value="" onValueChange={(v) => !busy && bulkAddToList(v)}>
        <SelectTrigger size="sm" className="w-36" disabled={busy}>
          <SelectValue placeholder="+ List…" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Quick lists</SelectLabel>
            {DEFAULT_LISTS.map((name) => (
              <SelectItem key={name} value={`__default:${name}`}>
                {name}
              </SelectItem>
            ))}
          </SelectGroup>
          {custom.length > 0 && (
            <SelectGroup>
              <SelectLabel>Your lists</SelectLabel>
              {custom.map((l) => (
                <SelectItem key={l.id} value={String(l.id)}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
          <SelectItem value="__new">➕ New list…</SelectItem>
        </SelectContent>
      </Select>
      <Button size="sm" variant="ghost" disabled={busy} onClick={onClear}>
        Clear
      </Button>
    </div>
  );
};
